export const AI_ENRICHMENT_TAB_INDEX = 5;
export const AI_SUMMARY_PREVIEW_LIMIT = 180;

export function isAiEnrichmentAction(actionType) {
  return actionType === "ai_enrichment" || String(actionType || "").startsWith("ai_enrichment");
}

export function getEnrichmentPromptLabel(actionType, options = {}) {
  if (actionType === "find_and_verify_email") return "Find Verified Emails";
  if (actionType === "find_email") return "Find Email";
  if (actionType === "verify_email") return "Verify Email";
  if (isAiEnrichmentAction(actionType)) {
    const query = String(options.query || "").trim();
    return query || "AI Agent";
  }
  return String(actionType || "Action");
}

function hasDisplayableValue(value) {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return Boolean(value.trim());
  if (Array.isArray(value)) return value.some(hasDisplayableValue);
  if (typeof value === "object") {
    return Object.values(value).some(hasDisplayableValue);
  }
  return true;
}

export function formatBannerContent(result) {
  if (!result) return null;
  const { status, actionType, title, detail, email, summary, result: rawResult, error } = result;

  if (status === "running") {
    return {
      title: "Running…",
      body: null,
      tone: "info",
    };
  }

  if (status === "failed") {
    return {
      title: "Action failed",
      body: error || "Something went wrong",
      tone: "error",
    };
  }

  if (actionType === "find_email") {
    return {
      title: title || "Email found",
      body: email || rawResult || null,
      tone: "success",
    };
  }

  if (actionType === "verify_email") {
    return {
      title: title || "Email verified",
      body: detail || (email ? `${email}${rawResult != null ? ` · Score ${rawResult}%` : ""}` : null),
      tone: "success",
    };
  }

  if (isAiEnrichmentAction(actionType)) {
    return {
      title: title || "AI insight",
      body: summary || rawResult || null,
      tone: "success",
      expandable: true,
    };
  }

  return {
    title: title || "Result",
    body: detail || summary || email || (rawResult != null ? String(rawResult) : null),
    tone: "success",
  };
}

export function formatEnrichmentHistoryResult(payload) {
  if (!payload) {
    return { text: "—", tone: "neutral" };
  }

  const {
    status,
    actionType,
    email,
    summary,
    result: rawResult,
    error,
    score,
    degraded,
    degradedNote,
    detail,
  } = payload;

  if (status === "running") {
    return {
      loading: true,
      variant: isAiEnrichmentAction(actionType) ? "long" : "short",
    };
  }

  if (status === "failed") {
    if (actionType === "find_email") {
      return {
        text: error === "Not found" || !error ? "Unable to find an email" : error,
        tone: "error",
      };
    }
    return {
      text: error || "Action failed — Try again",
      tone: "error",
    };
  }

  const hasAiResult = hasDisplayableValue(summary)
    || hasDisplayableValue(rawResult)
    || hasDisplayableValue(payload.derived_columns);

  if (
    isAiEnrichmentAction(actionType)
    && (status === "missing" || !hasAiResult)
  ) {
    return {
      text: detail || "No answer was returned. Try a different prompt.",
      tone: "missing",
    };
  }

  if (actionType === "find_email") {
    const address = email || rawResult;
    const outcome = payload.data?.status;
    if (outcome === 'found_unverified' || outcome === 'verified') {
      return { text: `${address} · ${outcome === 'verified' ? `Score ${payload.data.score}%` : 'Not verified'}`,
        tone: 'success' };
    }
    return {
      text: address || "—",
      tone: "success",
      isEmail: Boolean(address),
      email: address,
    };
  }

  if (actionType === "verify_email") {
    const resolvedScore = score ?? rawResult;
    const numericScore = Number(resolvedScore);
    if (!Number.isNaN(numericScore)) {
      return {
        text: numericScore >= 70 ? "Verified — deliverable" : `Verified — Score ${numericScore}%`,
        tone: "success",
      };
    }
    return { text: "Verified", tone: "success" };
  }

  if (isAiEnrichmentAction(actionType)) {
    const body = String(summary || rawResult || "").trim();
    const partialNote = degraded ? String(degradedNote || "Partial answer").trim() : "";
    const text = [partialNote, body].filter(Boolean).join("\n");
    return {
      text: text || "—",
      tone: "success",
      expandable: Boolean(text),
    };
  }

  return {
    text: String(detail || summary || email || rawResult || "—"),
    tone: "success",
  };
}
